'use client';

import { useCallback, useMemo } from 'react';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import {
  useGetReferralsV2Query,
  useExportReferralsMutation,
} from 'src/api/redux';
import { useTableFilterSelections } from '../filters';

interface GetReferralsV2Props {
  providerId: string;
  tableParams: any;
  location: string;
  customApiVolumeFilterFormat?: Record<string, any>;
}

interface ExportParams {
  fileFormat: string;
  columns: string[];
  fileName: string;
}

//NOTE: Valid direction and mode combinations are:
// valid_mode_combinations = [
//   # {direction, mode}
//   {"inbound", "sending"},
//   {"inbound", "treating"},
//   {"outbound", "receiving"}, aka outbound referrals
// ]

const useGetReferralsV2 = ({
  providerId,
  tableParams,
  location,
  customApiVolumeFilterFormat,
}: GetReferralsV2Props) => {
  const { apiFilterFormat, apiVolumeFilterFormat: hookApiVolumeFilterFormat } =
    useTableFilterSelections(location);

  // Use customApiVolumeFilterFormat if provided, otherwise use the one from the hook
  const apiVolumeFilterFormat =
    customApiVolumeFilterFormat || hookApiVolumeFilterFormat;

  const queryParams = useMemo(() => {
    const params = {
      ...tableParams,
    };

    const baseQueryParams = getSearchQueryParamsForAPI(params);

    const filterSetIds = apiFilterFormat
      .map(
        (id) => `&${encodeURIComponent('filter_set')}=${encodeURIComponent(id)}`
      )
      .join('');

    return baseQueryParams + filterSetIds;
  }, [tableParams, apiFilterFormat]);

  const data = useMemo(
    () => ({
      ...apiVolumeFilterFormat,
    }),
    [apiVolumeFilterFormat]
  );

  // Use RTK Query hook
  const {
    data: responseData,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetReferralsV2Query(
    { providerId, queryParams, data },
    {
      skip: !providerId,
    }
  );

  // Export mutation
  const [triggerExport] = useExportReferralsMutation();

  const exportReferrals = useCallback(
    async ({ fileFormat, columns, fileName }: ExportParams) => {
      try {
        await triggerExport({
          providerId,
          queryParams,
          data,
          fileFormat,
          columns,
          fileName,
        }).unwrap();
      } catch (error) {
        console.error('Export error:', error);
        throw error;
      }
    },
    [providerId, queryParams, data, triggerExport]
  );

  return {
    data: responseData,
    error,
    isLoading,
    isValidating: isFetching,
    mutate: refetch,
    exportReferrals,
  };
};

export default useGetReferralsV2;
